import {
  BankOutlined,
  FileTextOutlined,
  FormOutlined,
  PercentageOutlined,
  ProfileOutlined,
  TeamOutlined,
  UsergroupAddOutlined,
  CopyOutlined,
} from "@ant-design/icons";
import { useGetPermissionQuery } from "../../store/api/permission.api";
import { useCopyToClipboard } from "../../utils/Hooks/useCopyToClipboard";
import { getFullUrlPage } from "../../utils/utilities/getFullPageURL";
import { Menu } from "antd";
import { ReactElement, useMemo } from "react";
import {
  Link,
  useNavigate,
} from "react-router-dom";
import {
  STATUSES_TO_FILTER_AGENT,
  STATUSES_TO_FILTER_CLIENT,
  STATUSES_TO_FILTER_KO,
} from "../../constants/statusesToFilter";
import { ISidebarMenuProps } from "../../interfaces/props/ISidebarMenuProps";
import { useAppDispatch, useAppSelector } from "../../store/reduxHooks";
import { SELECT_PROFILE_ROLE } from "../../store/selectors/profileSelectors";
import { IconType } from "../../types/IconType";
import { USER } from "../../types/indexTypeEnum";
import { MenuItemType } from "../../types/MenuItemType";
import { IFilter } from "../../interfaces/IFilter";
import { APPLICATION_STATUS_ID } from "../../enums/applicationStatusIdEnum";
import { ItemType } from "antd/lib/menu/interface";
import { UP_DATE_APPLICATIONS_ALL } from "../../store/slice/applicationsSlice/applicationsSlice";

import classes from "./SiderbarMenu.module.css";

const ICONS_MAP: IconType = {
  FileTextOutlined: <FileTextOutlined />,
  BankOutlined: <BankOutlined />,
  ProfileOutlined: <ProfileOutlined />,
  TeamOutlined: <TeamOutlined />,
  PercentageOutlined: <PercentageOutlined />,
  UsergroupAddOutlined: <UsergroupAddOutlined />,
};

/**
 * Компонент бокового меню.
 *
 * @param {ISidebarMenuProps} props - Свойства, передаваемые в компонент бокового меню.
 * @param {IRouting[]} props.routes - Массив маршрутов приложения.
 * @param {string[]} props.selectedKeys - Массив ключей выбранных пунктов меню.
 * @param {boolean} props.collapsed - Флаг, указывающий, свернуто ли боковое меню.
 */
export function SidebarMenu(props: ISidebarMenuProps): ReactElement {
  const { routes, selectedKeys, collapsed } = props;
  const userRole = useAppSelector(SELECT_PROFILE_ROLE);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const { data: permissionData } = useGetPermissionQuery(undefined, {});

  const referral_code = permissionData?.data.referral_code;

  const rolesToCreateApplication: USER[] = [
    USER.CLIENT_SIMPLE,
    USER.AGENT_SIMPLE,
  ];

  function transformRouteKey(str: string) {
    return str === "/" ? str : `/${str}`;
  }

  function handleApplicationByStatusId(status: IFilter) {
    dispatch(UP_DATE_APPLICATIONS_ALL({data: [], last_page: 1}));
    if (status.id === APPLICATION_STATUS_ID.STATUS_SIGNATURE_REQUIRED && userRole === USER.CLIENT_SIMPLE) {
      navigate("/requiringDocs", { replace: true });
      return;
    }

    navigate("/?status_preset=" + encodeURIComponent(status.value), { replace: true });
  };

  const copyToClipboard = useCopyToClipboard();

  const menuItems = useMemo(() => {
    if (!routes) return [];

    const items: MenuItemType[] = [];

    if (rolesToCreateApplication.includes(userRole)) {
      items.push({
        key: "/application-create",
        icon: <FormOutlined />,
        label: (
          <Link
            data-cy={"link-layout-header-icon-create-application"}
            to={"/application-create"}
            className={classes.sideBarMenuCreateApplicationButton}
          >
            Создать заявку
          </Link>
        ),
      });
    }

    routes.forEach((item) => {
      item.routes?.forEach((subItem) => {
        if (!subItem.is_side) return;

        const icon = ICONS_MAP[subItem.icon as keyof typeof ICONS_MAP];
        if (!icon) {
          console.warn(`Icon not found for key: ${subItem.icon}`);
          return;
        }

        const itemKey = transformRouteKey(subItem.to);
        if (!itemKey) {
          console.warn(`Invalid key for route: ${subItem.to}`);
          return;
        }

        items.push({
          key: itemKey,
          icon: icon,
          label: (
            <Link
              data-cy={`link-layout-side-bar-${subItem.icon}`}
              to={subItem.to}
            >
              {subItem.content}
            </Link>
          ),
        });
      });
    });

    if (userRole === USER.AGENT_SIMPLE) {
      STATUSES_TO_FILTER_AGENT.forEach((item) => {
        items.push({
          key: item.value,
          icon: null,
          label: (
            <div
              className={classes.sideBarMenuFilterItem}
              onClick={() => handleApplicationByStatusId(item)}
            >
              {item.label}
            </div>
          ),
          onClick: () => {
            handleApplicationByStatusId(item);
          },
        });
      });
    }

    if (userRole === USER.SUPPORT_CREDIT) {
      STATUSES_TO_FILTER_KO.forEach((item) => {
        items.push({
          key: item.value,
          icon: null,
          label: (
            <div
              className={classes.sideBarMenuFilterItem}
              onClick={() => handleApplicationByStatusId(item)}
            >
              {item.label}
            </div>
          ),
          onClick: () => {
            handleApplicationByStatusId(item);
          },
        });
      });
    }

    if (userRole === USER.CLIENT_SIMPLE) {
      STATUSES_TO_FILTER_CLIENT.forEach((item) => {
        items.push({
          key: item.value,
          icon: null,
          label: (
            <div
              className={classes.sideBarMenuFilterItem}
              onClick={() => handleApplicationByStatusId(item)}
            >
              {item.label}
            </div>
          ),
          onClick: () => {
            handleApplicationByStatusId(item);
          },
        });
      });
    }

    if (referral_code) {
      const copyLink = getFullUrlPage(
        `/selectRegistration?referral_code=${referral_code}`,
      );
      const refMenuItems: ItemType = {
        label: !collapsed && <>Реферальная ссылка</>,
        type: "group",
        className: classes.refLinkItem,
        children: [
          {
            label: !collapsed ? (
              <>
                {copyLink} <CopyOutlined className={classes.copyIcon} />
              </>
            ) : (
              false
            ),
            key: "none",
            onClick: () => {
              copyToClipboard({
                text: copyLink,
              });
            },
            className: classes.refLinkItemChild,
            icon: collapsed ? <CopyOutlined /> : undefined,
          },
        ],
      };

      items.push(refMenuItems);
    }

    return items;
  }, [routes, userRole, dispatch, navigate, collapsed]);

  return (
    <Menu
      className={`${classes.sideBarMenu}${collapsed ? ` ${classes.sideBarMenuCollapsed}` : ""}`}
      mode="vertical"
      items={menuItems}
      selectedKeys={selectedKeys}
    />
  );
}
